<template>
	<template v-if="loadState === 'loaded'">
		<force-infos v-if="forceInfoAvailable" :responsibles="[ env.user.id ]" :src-type="oref.type" :src-id="oref.id"></force-infos>
		<object-layout v-if="subviewsAvailable" :name="saveName" :subviews="subviews" :build-url="addSubviewUrlParams">
			<template #hero><slot name="hero"></slot></template>
			<object-dialog :form="form" v-model="data" :display="{ mode: 'form'}" :errors="errors" :actions="actionControls" :readonly="ro" ref="objectDialog"></object-dialog>
		</object-layout>
		<object-dialog v-else :form="form" v-model="data" :display="{ mode: 'form'}" :errors="errors" :actions="actionControls" :readonly="ro" ref="objectDialog"></object-dialog>
	</template>
	<dialog v-else-if="isWarning" class="mode-form warning-dialog">
		<div class="dialog-head">
			<div class="dialog-head-left"><sym-icon :name="warning.icon"></sym-icon> {{ warning.title }}</div>
		</div>
		<div class="dialog-body">
			<div>{{ warning.body }}</div>
			<div v-if="helpers403.length > 0" class="helpers403">
				<label for="usersWithAccess">{{ env.msg('users.with.access') }}:</label>
				<select id="usersWithAccess">
					<option v-for="h in helpers403" :key="h.id" :value="h.id">{{ h.name }} ({{ userTags(h) }})</option>
				</select>
			</div>
		</div>
		<div class="dialog-foot">
			<div class="dialog-foot-left"><button type="button" class="sbutton" @click="goBack"><sym-icon name="caret-left-16"></sym-icon> {{ env.msg('butt.back')}}</button></div>
			<div class="dialog-foot-center"></div>
			<div class="dialog-foot-right"></div>
		</div>
	</dialog>
</template>

<script setup lang="ts">
/*
	RowObjectDialog adds load/save capability.
	To support this. The object and the form must be a regular row object (with assigned srcType and srcId passed in as RowObjectRef).
	This component doesn't handle any UI capabilities around the form except:
	- subviews display (splitter)
	- error dialog on loading error.
	- force info bar
	Use RowObjectPage to control sidebar menu content and stacker.
 */
import ForceInfos from "@/processes/ForceInfos.vue";
import SymIcon from "@fd/helpers/SymIcon.vue";
import ObjectLayout from "@/ui/subview/ObjectLayout.vue";
import {computed, reactive, ref, watch, nextTick, provide, onUnmounted, ComputedRef, useSlots} from "vue";
import {ApiError, ApiErrorResponse, HelpingUser} from "@t/util/ApiError.ts";
import {SubviewBadgeInfo, SubviewBadgePayload, SubviewTab} from "@t/ui/SubView.ts";
import {connectEventBus, injectEnv} from "@fd/Environment.ts";
import {
	hasIdentity,
	RowObjectRef,
	setEndpointId,
	SourceType,
	loadSourceType,
	loadFormDefinition,
	RowObjectType,
	RowObjectInit,
	sameRowObject, withIdentity
} from "@t/meta/SourceType.ts";
import {InterfaceRoot} from "@t/fd/InterfaceRoot.ts";
import {dialogControls, FormDefinition} from "@t/fd/layout/FormDefinition.ts";
import type {FormData, FormPart} from "@t/fd/FormPart.ts";
import ObjectDialog from "@/pages/row-object/ObjectDialog.vue";
import {addContextParams, AppContextMap, contextBuilder, contextKey, injectContext} from "@t/fd/AppContext.ts";
import type {ErrorMap} from "@fd/PartInjectKeys.ts";
import {useNavigationStore} from "@/store/NavigationStore.ts";
import {
	BEFORE_SAVE,
	CLEAR_ERRORS,
	CREATE,
	EDIT,
	FORBIDDEN,
	LOADING_ERROR,
	SAVING_PROBLEMS,
	NOT_FOUND,
	REPLACE_INIT_WITH_EDIT, BEFORE_LOAD
} from "@/pages/row-object/RowObjectEvents.ts";
import {DataWithMeta, removeMeta} from "@t/fd/WithMeta.ts";
import type {FormPartOrReference} from "@t/fd/FormPartOrReference.ts";
import {checkAccessKey, hasAccess, ObjectAccessFlag, readOnlyKey} from "@t/security/ObjectAccess.ts";
import {useForceInfoStore} from "@/store/ForceInfoStore.ts";
import type {DialogDisplay} from "@t/ui/Dialog.ts";
import get from "lodash.get";

const props = withDefaults(defineProps<{
	/** Reference to an object to be displayed (new or existing) */
	oref: RowObjectType
	/** Name used to save subview layout*/
	name?: string;
	/** How to form will be displayed */
	display?: DialogDisplay,

	/** List of buttons supported by the parent component */
	buttons?: FormPartOrReference[];
	/** Prevent adding standard Save button, defaults to true */
	addSave?: boolean;
	/** Prevent adding standard SaveAndBack button, defaults to true */
	addSaveAndBack?: boolean;

	/** Display force info component */
	forceInfo?: boolean;

	/** Errors received as a result of processing an action */
	parentErrors?: ErrorMap;

	/** Display subviews around the form (if available) */
	subviews?: boolean;

	/**
	 * This is primary dialog on the page
	 * App context of the primary dialog is used to build sidebar menu. Defaults to "false".
	 **/
	primary?: boolean;

	/** Can be used to alter subview URL (if not component based) */
	buildSubviewUrl?: (tab:SubviewTab) => string;
	/** Parent component can add extra params to /api/subviews/badges endpoint call */
	buildBadgeUrl?: (endpoint:string) => string;

	/* handlers to "hack" behavior */
	buildEndpoint?: (endpoint:string, edit:boolean, type:SourceType) => string;
}>(), {
	display: () => <DialogDisplay>{ mode: 'form' },
	buttons: () => [],
	addSave: true,
	addSaveAndBack: true,
	parentErrors: () => [],
	forceInfo: false,
	buildEndpoint: (endpoint:string, e:boolean, t:SourceType) => endpoint,
	subviews: false,
	primary: false
});

// Injections
const env = injectEnv();
const ns = useNavigationStore();
const bus = connectEventBus();
const fis = useForceInfoStore();

// Data
const form = ref<InterfaceRoot<FormDefinition>|null>(null);
const data = defineModel<FormData|null>({ required:true });
const badges = reactive<SubviewBadgePayload>([]);
const type = ref<SourceType|null>(null);
const objectDialog = ref<InstanceType<typeof ObjectDialog> | null>(null);

const warnings:ErrorMap = reactive<ErrorMap>([]);
const localErrors:ErrorMap = reactive<ErrorMap>([]);
const helpers403 = reactive<HelpingUser[]>([]);

/** Combine all error sources into one */
const errors = computed<ErrorMap>(() => {
	const e:ErrorMap = [];
	if (Array.isArray(props.parentErrors) && props.parentErrors.length > 0) e.push(...props.parentErrors);
	e.push(...localErrors);
	e.push(...warnings);
	return e;
});

type LoadingState = "initial" | "loading" | "loaded" | "notfound" | "forbidden" | "error";
const loadState = ref<LoadingState>("initial");
const okLoaded = computed<boolean>(() => loadState.value === 'loaded');

const saveName = computed<string>(() => props.name ?? ("object-"+props.oref.type));

const forceInfoAvailable = computed<boolean>(() => {
	return props.forceInfo && props.display.mode === "form" && !!env?.user?.id && hasIdentity(props.oref);
});

const slots = useSlots();
const hasHero = computed<boolean>(() => {
	const content = slots.hero?.();
	return content != null && content.length > 0;
});

const subviewsAvailable = computed<boolean>(() => {
	return props.display.mode === "form" && (props.subviews && subviews.value.length > 0 || hasHero.value);
});
const isWarning = computed<boolean>(() => warning.value != null);

const actionControls = computed<FormPart>(() => {
	const btns = [ ...props.buttons ];
	if (props.addSave) btns.push("save");
	if (props.addSaveAndBack) btns.push("saveAndBack");
	return dialogControls(env, btns);
});

const ro = computed<boolean>(() => {
	const access = hasAccess(data.value, "edit");
	return !access || fis.userBlocks;
});

function checkAccess(...flags:ObjectAccessFlag[]):boolean {
	return hasAccess(data.value, ...flags);
}

provide(readOnlyKey, ro);
provide(checkAccessKey, checkAccess);


// Combine parent context with context provided by the form and its data
const parentContext = injectContext();
const ctx = computed<AppContextMap>(() => {
	if (form.value && data.value) {
		return contextBuilder(form.value, data.value, parentContext.value);
	} else {
		return parentContext.value;
	}
});
provide(contextKey, ctx);

/** Return AppContext of the dialog */
function getDialogContext():ComputedRef<AppContextMap> {
	return ctx;
}



interface WarningInfo {
	icon: string;
	title: string;
	body: string;
}

const warning = computed<WarningInfo|null>(() => {
	if (loadState.value === 'notfound') {
		return { icon: "not-found", title: env.msg('record.notFound'), body: env.msg('record.notFound.desc', (props.oref as RowObjectRef).id) };
	} else if (loadState.value === 'forbidden') {
		return { icon: "forbidden", title: env.msg('record.forbidden'), body: env.msg('record.forbidden.desc', (props.oref as RowObjectRef).id) };
	} else if (loadState.value === 'error') {
		return { icon: "heart", title: env.msg('error.internal'), body: env.msg('error.internal.desc') };
	}
	return null;
});

async function loadWarnings() {
	if (ctx?.value.customer || ctx?.value.person || ctx?.value.branch) {
		let url = "api/warnings/form";
		const nr = await env.net.fetch(url, 'POST', ctx.value);
		if (nr.ok) {
			const errs:ErrorMap = await nr.json() as ErrorMap;
			warnings.length = 0;
			warnings.push(...errs);
		}
	}
}
watch(ctx, loadWarnings);

function userTags(u:HelpingUser):string {
	const tags:string[] = [];
	if (u.owner) tags.push(env.msg('user.role.owner'));
	if (u.salesman) tags.push(env.msg('user.role.salesman'));
	if (u.responsible) tags.push(env.msg('user.role.responsible'));
	if (u.admin) tags.push(env.msg('user.role.admin'));
	return tags.join(', ');
}

const subviews = computed<SubviewTab[]>(() => {
	if (type.value && Array.isArray(type.value.subviews)) {
		const all = type.value.subviews;
		const filtered:SubviewTab[] = [];
		const hasId = hasIdentity(props.oref);
		for (let sub of all) {
			if (!sub.show || (!hasId && !sub.showOnCreate)) continue;
			const subview = { ...sub };
			const bi = findBadgeFor(sub.name);
			if (bi?.badge) {
				subview.badge = bi.badge;
			}
			filtered.push(subview);
		}
		return filtered;
	} else return [];
});

// LOADING and INIT
watch(() => props.oref, async (r:RowObjectRef) => {
	loadState.value = "loading";
	helpers403.length = 0;
	try {
		bus.fire(CLEAR_ERRORS, props.oref);
		bus.fire(BEFORE_LOAD, props.oref);	// Let parents to do some maintenance steps
		ns.chatContext = null;
		if (type.value == null || type.value.id !== r.type) {
			type.value = await loadSourceType(env, r.type);
		}
		const metaSupport = !!type.value.meta;	// If endpoints support metadata they can load data and corresponding form at once
		let ep = buildEndpoint();

		if (metaSupport) ep = env.net.urlParam(ep, "meta", "true");
		else if (type.value.form) form.value = await loadFormDefinition(env, type.value.form);

		const resp = await env.net.fetch(ep);
		if (resp.ok) {
			if (metaSupport) {
				const dataMeta:DataWithMeta<FormData> = await resp.json();
				//console.log("dataMeta:"+JSON.stringify(dataMeta));
				form.value = dataMeta.META.form;
				data.value = removeMeta(dataMeta);
			} else {
				data.value = await resp.json();
			}
			loadState.value = "loaded";
			await nextTick();
			if (hasIdentity(props.oref)) {
				bus.fire(EDIT, props.oref);
				ns.display(type.value.id, props.oref.id);
				await loadSubviewBadges(props.oref);
			} else {
				bus.fire(CREATE, props.oref);
			}
		} else if (resp.status === 403 /* Forbidden */) {
			loadState.value = "forbidden";
			const apiErr:ApiErrorResponse = await resp.json();
			if (Array.isArray(apiErr.helpers)) helpers403.push(...apiErr.helpers);
			ns.forward(env.msg('record.forbidden'));
			bus.fire(FORBIDDEN, props.oref);
		} else if (resp.status === 404 /* Not found */) {
			loadState.value = "notfound";
			ns.forward(env.msg('record.notFound'));
			bus.fire(NOT_FOUND, props.oref);
		} else {
			console.error("Cannot load form data with META info from " + ep);
			loadState.value = "error";
			bus.fire(LOADING_ERROR, props.oref, ep);
		}
	} catch (ex) {
		loadState.value = "error";
		console.error(ex);
		bus.fire(LOADING_ERROR, props.oref, ex);
	}
}, { immediate: true });


bus.on(CLEAR_ERRORS, (oref:RowObjectType) => {
	if (sameRowObject(props.oref, oref)) localErrors.length = 0;
});

function getIdOf(data?:FormData, type?:SourceType):string|number|null {
	if (data == null || type == null) return null;
	const idProperty = type.idProperty ?? "id";
	const id = get(data, idProperty);
	if (typeof id === "number" || typeof id === "string") return id;
	return null;
}

// SAVING
let saving = false;	// saving guard
bus.onCommand(["save", "saveAndBack", "saveOnly"], (cmd:string) => {
	return new Promise(async (resolve) => {
		//const stay = cmd === "save";
		if (!saving && loadState.value === "loaded") {
			try {
				saving = true;
				bus.fire(CLEAR_ERRORS, props.oref);
				bus.fire(BEFORE_SAVE, props.oref);
				document.body.style.cursor = "progress";
				let ep:string, method:string;
				if (hasIdentity(props.oref)) {
					ep = setEndpointId("api" + type.value.edit, props.oref.id);
					method = "PUT";
				} else {
					ep = "api" + type.value.create;
					method = "POST";
				}
				const nr = await env.net.fetch(ep, method, data.value);
				if (nr.ok) {
					// Successfully saved ... body should be the new version of the record
					const record = await nr.json() as FormData;
					resolve("ok");	// this will cause propper feedback to the user
					data.value = record;
					if (cmd === "save") {
						await nextTick();
						// Set form to NOT-dirty
						if (objectDialog.value) objectDialog.value.setDirty(false);
						bus.fire(REPLACE_INIT_WITH_EDIT, withIdentity(props.oref, getIdOf(data.value, type.value)));	// Start edit on the same page
					} else if (cmd === "saveAndBack") {
						console.log("going back: ------------------------------------------------");
						ns.back();
					}
				} else {
					// Backend returned non-20xx status code (e.g. BAD_REQUEST)
					const er = await nr.json() as ApiErrorResponse;
					resolve("fail");	// this will cause propper feedback to the user
					bus.fire(SAVING_PROBLEMS, props.oref);
					processSubmitProblems(er.problems);
				}
			} catch(err) {
				resolve("fail");
				const errText = err.toString();
				processSubmitProblems([{ type: "ERROR", level: "SEVERE", message:errText }]);
				console.error(err);
			} finally {
				saving = false;
				document.body.style.cursor = "auto";
			}
		} else resolve("fail");
	});
});

function processSubmitProblems(problems:ApiError[]):void {
	console.log("Processing problems: ", problems);
	if (!Array.isArray(problems)) return;
	localErrors.push(...problems);
}


function findBadgeFor(name:string):SubviewBadgeInfo|undefined {
	return badges.find((bi) => name === bi.name);
}

function addSubviewUrlParams(tab:SubviewTab):string {
	let url = typeof props.buildSubviewUrl === "function" ? props.buildSubviewUrl(tab) : tab.src;
	if (!url) throw new Error("URL not defined for subview "+tab.name);
	url = env.net.urlParam(url, "subSrcType", props.oref.type);
	if (props.oref.subType) url = env.net.urlParam(url, "subSrcSubType", props.oref.subType);
	if (hasIdentity(props.oref)) url = env.net.urlParam(url, "subSrcId", props.oref.id);
	return url;
}


function buildEndpoint():string {
	let ep:string;
	let isEdit:boolean;
	let initCtx:AppContextMap;
	if (hasIdentity(props.oref)) {
		ep = setEndpointId("api"+type.value.edit, props.oref.id);
		initCtx = {};
		isEdit = true;
	} else {
		ep = "api" + type.value.init;
		initCtx = { ...ctx.value };
		isEdit = false;
	}
	if (props.oref.params) {
		for (const [key, value] of Object.entries(props.oref.params)) {
			if (value != null) initCtx[key] = value;
		}
	}
	ep = addContextParams(ep, initCtx);
	ep = props.buildEndpoint(ep, isEdit, type.value);
	return ep;
}


async function loadSubviewBadges(r:RowObjectRef):Promise<any> {
	if (Array.isArray(type.value?.subviews)) {
		const all = type.value.subviews;
		const query:SubviewBadgeInfo[] = [];
		for (let sub of all) {
			if (sub.show && sub.counted) query.push({ name: sub.name });
		}

		if (query.length > 0) {
			let ep = env.net.urlParam("api/subviews/badges", "subSrcType", r.type);
			if (r.subType) ep = env.net.urlParam(ep, "subSrcSubType", r.subType);
			if (hasIdentity(r)) ep = env.net.urlParam(ep, "subSrcId", r.id);
			if (typeof props.buildBadgeUrl === "function") ep = props.buildBadgeUrl(ep);

			const resp = await env.net.fetch(ep, "POST", query);
			if (resp.ok) {
				const bgs:SubviewBadgePayload = await resp.json();
				badges.length = 0;
				badges.push(...bgs);
			}
		}
	}
}

defineExpose({
	getDialogContext,
	setDirty: (dirtyFlag:boolean) => {
		if (objectDialog.value) objectDialog.value.setDirty(dirtyFlag);
	},
	setReadOnly: (flag:boolean) => {
		if (objectDialog.value) objectDialog.value.setReadOnly(flag);
	},
	setViewMode: (flag:boolean) => {
		if (objectDialog.value) objectDialog.value.setViewMode(flag);
	},
	/** Animated close of the dialog window */
	closeDialog: ():Promise<boolean> => {
		if (objectDialog.value) return objectDialog.value.closeDialog();
		else return Promise.resolve(false);
	},
	/** Animated shake of the dialog window */
	shakeDialog: ():Promise<boolean> => {
		if (objectDialog.value) return objectDialog.value.shakeDialog();
		else return Promise.resolve(false);
	},
	okLoaded: ():boolean => okLoaded.value
});

</script>

<style>
.helpers403 {
	margin-top: 16px;
	display: flex;
	flex-direction: row;
	column-gap: 4px;
	align-items: baseline;
}
</style>